import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Lock, Download, CheckCircle2, ArrowRight, ShieldCheck, BookOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchResources, downloadResourceUrl } from "../lib/api";
import { Seo } from "../components/Seo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startSignInFromDownloads = () => {
  const redirectUrl = window.location.origin + "/downloads?claim=advanced-peptide-protocols";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Downloads() {
  const { user, loading } = useAuth();
  const [resources, setResources] = useState([]);
  const [params, setParams] = useSearchParams();
  const claim = params.get("claim");
  const claimed = useRef(false);

  useEffect(() => {
    fetchResources().then(setResources).catch(() => {});
  }, []);

  // Auto-download the moment we land back post-auth with ?claim=<slug>
  useEffect(() => {
    if (loading) return;
    if (!user) return;
    if (!claim || claimed.current) return;
    claimed.current = true;
    // Trigger a fresh navigation to the auth-gated file. The browser sends
    // the httpOnly session_token cookie automatically and the response is a
    // Content-Disposition: attachment PDF.
    window.location.href = downloadResourceUrl(claim);
    // Clear the ?claim= flag so a refresh doesn't re-download.
    setTimeout(() => setParams({}, { replace: true }), 250);
    toast.success("Preparing your PDF…");
  }, [user, loading, claim, setParams]);

  const handleDownload = (slug) => {
    if (!user) {
      startSignInFromDownloads();
      return;
    }
    window.location.href = downloadResourceUrl(slug);
    toast.success("Preparing your PDF…");
  };

  return (
    <div data-testid="downloads-page" className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
      <Seo title="Research Downloads — Advanced Peptide Protocols" description="Download the Advanced Peptide Protocols PDF and other research resources from Peptide Labs N.I." />
      <div className="max-w-3xl">
        <div className="label-mono">Research Resources</div>
        <h1 className="mt-4 text-4xl lg:text-6xl tracking-tight text-white font-medium">
          Practitioner-grade downloads.
        </h1>
        <p className="mt-4 text-[#B7BCC7] max-w-xl">
          Deeper laboratory references authored by our lab team. Free for registered researchers —
          sign in with Google to unlock the library.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {resources.map((r) => (
          <article
            key={r.slug}
            data-testid={`resource-card-${r.slug}`}
            className="lg:col-span-12 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center border border-[#2A2A2A] rounded-3xl bg-[#0e0e0e] overflow-hidden"
          >
            {/* Cover mock */}
            <div className="lg:col-span-5 relative aspect-[4/5] max-h-[420px] w-full">
              <div className="absolute inset-0 grid-backdrop opacity-30" />
              <div className="absolute inset-6 rounded-2xl border border-[#2A2A2A] bg-[#0B0B0B] shadow-lab-glow-lg p-8 flex flex-col">
                <div className="label-mono">Peptide Labs N.I.</div>
                <div className="mt-6 text-white text-2xl font-medium tracking-tight leading-tight">
                  Advanced <br /> Peptide<br /> Protocols
                </div>
                <div className="mt-auto flex items-center justify-between text-[10px] font-mono text-[#6E7784]">
                  <span>REV {r.revision}</span>
                  <span>{r.pages} PAGES</span>
                </div>
                <div className="absolute inset-x-8 bottom-24 h-px bg-[#1565FF]/40" />
              </div>
              <div className="absolute top-6 right-6 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#0B0B0B]/80 border border-[#1565FF]/40">
                <FileText size={11} className="text-[#1565FF]" />
                <span className="text-[10px] font-mono tracking-widest text-white">PDF</span>
              </div>
            </div>

            {/* Meta */}
            <div className="lg:col-span-7 p-8 lg:p-10 lg:pr-12">
              <div className="label-mono">{r.subtitle}</div>
              <h2 className="mt-3 text-3xl lg:text-4xl tracking-tight text-white font-medium">
                {r.title}
              </h2>
              <p className="mt-4 text-[#B7BCC7] leading-relaxed">
                {r.description}
              </p>

              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px] text-[#B7BCC7]">
                {[
                  "Storage & cold-chain",
                  "Reconstitution SOP",
                  "Dilution reference table",
                  "Injection technique",
                  "CoA reading",
                  "Dose-calculation formulas",
                  "Cross-contamination",
                  "Compound-specific notes",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-[#1565FF]" /> {t}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                {user ? (
                  <button
                    onClick={() => handleDownload(r.slug)}
                    data-testid={`resource-download-${r.slug}`}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <Download size={16} /> Download PDF
                  </button>
                ) : (
                  <button
                    onClick={startSignInFromDownloads}
                    data-testid={`resource-signin-${r.slug}`}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    <Lock size={16} /> Sign in to Unlock
                  </button>
                )}
                <Link to="/knowledge" className="btn-ghost inline-flex items-center gap-2">
                  <BookOpen size={14} /> Read the Knowledge Base
                </Link>
              </div>

              <div className="mt-6 flex items-center gap-2 text-[11px] text-[#6E7784] tracking-widest uppercase font-mono">
                <ShieldCheck size={12} className="text-[#1565FF]" />
                Free for verified researchers · No email spam · One-click download
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
